import api from "./axios";
import {
  FilaChamado,
  FilaChamadosPagina,
  FilaMembro,
  FilaOrganizacao,
  FilaRequest,
} from "../interfaces/FilaOrganizacao";
import { TicketState } from "../interfaces/Ticket";
import { mensagemDoServidor } from "../utils/mensagemDoServidor";

/**
 * Fila de atendimento da PRÓPRIA organização do cliente. Sem `clientId` em rota ou corpo: a
 * organização é o `org_id` do token e o backend reconfere o vínculo ativo a cada chamada.
 */
const BASE = "/ClientPortal/fila";

export const getChamadosDaFila = async (
  filtro: { filaId?: number; estado?: TicketState; pagina?: number }
): Promise<FilaChamadosPagina> =>
  (await api.get(`${BASE}/chamados`, { params: { filaId: filtro.filaId, estado: filtro.estado, pagina: filtro.pagina ?? 1 } })).data;

export const getChamadoDaFila = async (id: number): Promise<FilaChamado> =>
  (await api.get(`${BASE}/chamados/${id}`)).data;

export const assumirChamado = async (id: number): Promise<void> => {
  await api.post(`${BASE}/chamados/${id}/assumir`);
};

export const atribuirChamado = async (id: number, pessoaId: number): Promise<void> => {
  await api.post(`${BASE}/chamados/${id}/atribuir`, { pessoaId });
};

export const mudarEstadoChamado = async (id: number, state: TicketState): Promise<void> => {
  await api.post(`${BASE}/chamados/${id}/estado`, { state });
};

export const moverChamado = async (id: number, filaId: number): Promise<void> => {
  await api.post(`${BASE}/chamados/${id}/mover`, { filaId });
};

export const encaminharParaDataValue = async (id: number): Promise<void> => {
  await api.post(`${BASE}/chamados/${id}/encaminhar-datavalue`);
};

/** `isInternal=true` é nota da organização (o solicitante não vê); false é resposta pública. */
export const comentarChamado = async (id: number, body: string, isInternal: boolean): Promise<void> => {
  await api.post(`${BASE}/chamados/${id}/comentarios`, { body, isInternal });
};

/*
  Coordenação de filas (listar, criar/editar, membros). Sem `orgId`: a organização do token
  (`/ClientPortal/fila/...`, `itsm.fila.coordenar`). Com `orgId`: `/clientes/{orgId}/filas...`,
  para quem gere a equipe do cliente (`cliente.equipe.gerir`, inclusive o interno). As duas
  famílias aplicam as mesmas regras no servidor.
*/
const filasBase = (orgId?: number) => (orgId === undefined ? `${BASE}/filas` : `/clientes/${orgId}/filas`);

export const getFilasDaOrganizacao = async (orgId?: number): Promise<FilaOrganizacao[]> => {
  const { data } = await api.get(filasBase(orgId));
  return Array.isArray(data) ? data : [];
};

export const criarFila = async (corpo: FilaRequest, orgId?: number): Promise<FilaOrganizacao> =>
  (await api.post(filasBase(orgId), corpo)).data;

/** O PUT SUBSTITUI `clientProjectId`: `corpo` deve trazer o valor atual para não desligar a fila do projeto. */
export const atualizarFila = async (id: number, corpo: FilaRequest, orgId?: number): Promise<FilaOrganizacao> =>
  (await api.put(`${filasBase(orgId)}/${id}`, corpo)).data;

export const getMembrosDaFila = async (id: number, orgId?: number): Promise<FilaMembro[]> => {
  const { data } = await api.get(`${filasBase(orgId)}/${id}/membros`);
  return Array.isArray(data) ? data : [];
};

/** Candidatos a membro (coordenador): vínculo ativo e perfil com `itsm.fila.atender`. */
export const getAtendentesDaOrganizacao = async (orgId?: number): Promise<FilaMembro[]> => {
  const { data } = await api.get(orgId === undefined ? `${BASE}/atendentes` : `${filasBase(orgId)}/atendentes`);
  return Array.isArray(data) ? data : [];
};

export const definirMembrosDaFila = async (id: number, pessoaIds: number[], orgId?: number): Promise<void> => {
  await api.put(`${filasBase(orgId)}/${id}/membros`, { pessoaIds });
};

/** Erros de regra chegam como `{ message }` ou texto puro (400); o resto cai na mensagem padrão. */
export const mensagemDaFila = mensagemDoServidor;
